import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import Badge from '@mui/material/Badge';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { makeStyles } from 'tss-react/mui';

import ParentFilterContext from './ParentFilterContext';
import StyledTreeItem from './StyledTreeItem';

const useStyles = makeStyles()({
  root: {
    flexGrow: 1,
    maxWidth: 400,
    width: '100%',
  },
  collectionLabel: {
    textAlign: 'center',
    color: '#2C367E',
  },
  customBadge: {
    backgroundColor: '#2C367E',
    color: 'white',
    right: '12px',
  },
  MuiBadgeRoot: {
    width: '100%',
  },
  MuiTreeItemLabel: {
    height: '50px',
  },
});

const compare = (a: any, b: any) => a.position > b.position;

interface SubEntry {
  id: string | number;
  label: string;
  icon?: string;
  description?: string;
  subEntries?: SubEntry[];
  position?: number;
  [key: string]: any;
}

interface Entry {
  id: string | number;
  label: string;
  color?: string;
  icon?: string;
  description?: string;
  subEntries: SubEntry[];
}

interface CheckboxState {
  id: string | number;
  label: string;
  icon?: string;
  color?: string;
  description?: string;
  checked: boolean;
  isSubEntries?: boolean;
  isSubSubEntries?: boolean;
  subEntries?: SubEntry[];
}

interface ParentContainerProps {
  entry: Entry;
  onCategoryChange: (checkboxes: CheckboxState[]) => void;
  isForm?: boolean;
  initialCheckedEntries?: (string | number)[];
}

function ParentContainer({ entry, onCategoryChange, isForm, initialCheckedEntries }: ParentContainerProps) {
  const { classes } = useStyles();

  const [parentCheckboxChecked, setParentCheckboxChecked] = useState(false);
  const [checkboxes, setCheckboxes] = useState<CheckboxState[]>([]);
  const [expanded, setExpanded] = useState<(string | number)[]>([]);
  const [nodesArray, setNodesArray] = useState<(string | number)[]>([]);
  const isFirstRef = useRef(true);

  useEffect(() => {
    const newCheckboxesState: CheckboxState[] = [];
    const newNodesArray: (string | number)[] = [entry.id];

    const checkedSet = new Set((initialCheckedEntries || []).map(String));

    [...entry.subEntries].sort(compare).map(({ id, label, icon, description, subEntries }) => {
      newCheckboxesState.push({
        id,
        label,
        icon,
        color: entry.color,
        description,
        checked: checkedSet.has(String(id)),
        isSubEntries: true,
        subEntries: subEntries || [],
      });
      newNodesArray.push(id);
      if (subEntries != undefined) {
        [...subEntries].sort(compare).map(({ id, label, icon, description }) => {
          newCheckboxesState.push({
            id,
            label,
            icon,
            color: entry.color,
            description,
            checked: checkedSet.has(String(id)),
            isSubEntries: true,
            isSubSubEntries: true,
          });

          newNodesArray.push(id);
        });
      }
    });

    setNodesArray(newNodesArray);
    setCheckboxes(newCheckboxesState);

    if (checkedSet.size > 0) {
      const hasChecked = newCheckboxesState.some(cb => cb.checked);
      const allChecked = newCheckboxesState.length > 0 && newCheckboxesState.every(cb => cb.checked);
      setParentCheckboxChecked(allChecked);
      if (hasChecked) {
        onCategoryChange(newCheckboxesState);
      }
    }
  }, []);

  const updateParentWithChildren = useCallback(() => {
    if (entry.subEntries && entry.subEntries.length > 0) {
      const allChecked = checkboxes.filter(({ checked }) => checked).length === checkboxes.length;

      setParentCheckboxChecked(allChecked);
    }
  }, [entry.subEntries, checkboxes]);

  useEffect(() => {
    if (isFirstRef.current) {
      isFirstRef.current = false;
      return;
    }

    updateParentWithChildren();
  }, [checkboxes]);

  const handleToggle = useCallback((_event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
  }, []);

  const checkHandleToggle = useCallback(() => {
    if (expanded.length === 0) {
      setExpanded(nodesArray as string[]);
    }
  }, [expanded, nodesArray]);

  const handleParentCheckboxChange = useCallback((isChecked: boolean) => {
    if (entry.subEntries && entry.subEntries.length > 0) {
      const newChildrenCheckState = [...checkboxes].map((aCheckbox) => ({
        ...aCheckbox,
        checked: isChecked,
      }));

      setCheckboxes(newChildrenCheckState);
      onCategoryChange(newChildrenCheckState);
    } else {
      onCategoryChange([
        {
          ...entry,
          checked: isChecked,
        } as CheckboxState,
      ]);
    }

    setParentCheckboxChecked(isChecked);
  }, [checkboxes]);

  const handleChildCheckboxChange = useCallback((isChecked: boolean, index: string | number) => {
    const clickedCheckbox = checkboxes.find(cb => cb.id == index);

    let newCheckState = checkboxes.map((aCheckbox) => {
      if (index == aCheckbox.id) {
        return { ...aCheckbox, checked: isChecked };
      }

      if (clickedCheckbox && clickedCheckbox.subEntries && clickedCheckbox.subEntries.length > 0) {
        const isChildOfClicked = clickedCheckbox.subEntries.some(sub => sub.id === aCheckbox.id);
        if (isChildOfClicked && aCheckbox.isSubSubEntries) {
          return { ...aCheckbox, checked: isChecked };
        }
      }

      return aCheckbox;
    });

    newCheckState = newCheckState.map((aCheckbox) => {
      if (aCheckbox.isSubEntries && !aCheckbox.isSubSubEntries && aCheckbox.subEntries && aCheckbox.subEntries.length > 0) {
        const allChildrenChecked = aCheckbox.subEntries.every(sub => {
          const childCheckbox = newCheckState.find(cb => cb.id === sub.id);
          return childCheckbox && childCheckbox.checked;
        });

        if (allChildrenChecked && !aCheckbox.checked) {
          return { ...aCheckbox, checked: true };
        } else if (!allChildrenChecked && aCheckbox.checked) {
          const isAnyChildUnchecked = aCheckbox.subEntries.some(sub => {
            const childCheckbox = newCheckState.find(cb => cb.id === sub.id);
            return childCheckbox && !childCheckbox.checked;
          });
          if (isAnyChildUnchecked) {
            return { ...aCheckbox, checked: false };
          }
        }
      }
      return aCheckbox;
    });

    setCheckboxes(newCheckState);
    onCategoryChange(newCheckState);
  }, [onCategoryChange, checkboxes]);

  const numberChecked = useMemo(() => {
    return checkboxes.filter(({ checked }) => checked).length;
  }, [checkboxes]);

  return (
    <ParentFilterContext.Provider
      value={{
        checked: false,
        handleParentCheckboxChange,
        handleChildCheckboxChange,
        checkHandleToggle,
      }}
    >
      <Badge
        badgeContent={numberChecked}
        showZero={false}
        className={classes.root}
        classes={{ badge: classes.customBadge }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <TreeView
          className={classes.root}
          aria-label="controlled"
          expanded={expanded as string[]}
          onNodeToggle={handleToggle}
          defaultCollapseIcon={<ArrowDropDownIcon />}
          defaultExpandIcon={<ArrowRightIcon />}
          defaultEndIcon={<div style={{ width: 24 }} />}
          disableSelection
        >
          <StyledTreeItem
            key={entry.id}
            nodeId={entry.id}
            labelText={entry.label}
            color={entry.color}
            description={entry.description}
            icon={entry.icon}
            isParent
            hasSubEntries={entry.subEntries && entry.subEntries.length > 0}
            checked={parentCheckboxChecked}
            isForm={isForm}
          >
            {checkboxes.filter(subEntry => !subEntry.isSubSubEntries).map((subEntry) => {
              const subSubEntries = checkboxes.filter(item =>
                item.isSubSubEntries &&
                subEntry.subEntries &&
                subEntry.subEntries.some(sub => sub.id === item.id),
              );

              return (
                <StyledTreeItem
                  key={subEntry.id}
                  nodeId={subEntry.id}
                  id={subEntry.id}
                  labelText={subEntry.label}
                  checked={subEntry.checked}
                  description={subEntry.description}
                  color={entry.color}
                  icon={subEntry.icon}
                  isForm={isForm}
                  hasSubEntries={subSubEntries.length > 0}
                >
                  {subSubEntries.map((subSubEntry) => {
                    return (
                      <StyledTreeItem
                        key={subSubEntry.id}
                        id={subSubEntry.id}
                        nodeId={subSubEntry.id}
                        labelText={subSubEntry.label}
                        checked={subSubEntry.checked}
                        description={subSubEntry.description}
                        isForm={isForm}
                        icon={subSubEntry.icon}
                        color={entry.color}
                      />
                    );
                  })}
                </StyledTreeItem>
              );
            })}
          </StyledTreeItem>
        </TreeView>
      </Badge>
    </ParentFilterContext.Provider>
  );
}

export default ParentContainer;
